<template lang="pug">
  .play-box
    template(v-if="!isLoading")
      game-header(
        @soundStateChanged="headerKey = !headerKey",
        :key="headerKey",
        :time="getTime",
        :slug="type",
        :game-type="parameters && parameters.courseType",
        :is-homework="!!exerciseId",
      )
      .play-box__game(
        v-if="!gameFinished && gameStarted && !isNextExampleButtonShown"
      )
        .play-box__example
          .play-box__example-info-container
            .play-box__search-number-container
              .play-box__header
                | {{$i18n.t('common.common_gameName_assembleTheNumber')}}:
              .play-box__search-number-wrapper
                .play-box__search-number.play-box__search-number_default
                  | {{ searchNumber }}
            .game-info__container
              .game-info__block
              game-info(
                :score="{left: goodAnswers, right: badAnswers}",
                :parameters="parameters",
                :game-allows-tips="true",
                :is-need-to-use-default-position-for-tips-block="true"
              )
          assemble-the-number-table(
            :assemble-the-number-table="assembleTheNumberTable"
            :number-of-characters="+parameters.digitCapacity"
            :is-colored-cells="parameters.coloredNumbers"
            @handleClick="handleClick"
          )
      .play-box__end-container(v-if="gameFinished || isNextExampleButtonShown")
        fireworks(
          v-if="gameFinished"
          :is-mobile="isMobile"
        )
        .play-box__reaction
          .play-box__reaction-side.play-box__reaction-side--correct(
            v-if="!isNextExampleButtonShown"
          )
            div
              | {{ $i18n.t('trainer.trainer_gamePlayBox_correct') }}
            .play-box__reaction-count
              | {{ goodAnswers }}
          result-image.play-box__reaction-image(:status="'success'")
          .play-box__reaction-side.play-box__reaction-side--incorrect(
            v-if="!isNextExampleButtonShown"
          )
            div
              | {{ $i18n.t('trainer.trainer_gamePlayBox_wrong') }}
            .play-box__reaction-count
              | {{ badAnswers }}
        result-buttons(
          :exercise-id="exerciseId"
          :games-type="parameters.courseType"
          show-restart-button
          @restartGame="restartGame"
        )
          template(slot="mainButton")
            cs-button.play-box__button(
              v-if="isNextExampleButtonShown"
              color="blue"
              @click="getExample"
            )
              | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
          slot
    cs-loader(v-else)
</template>

<script>
    import AssembleTheNumberTable from './AssembleTheNumberTable.vue';
    import { cloneDeep, chunk, shuffle } from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import EndGameMixin from '../../../../utils/trainer/endGame';
    import GameHeader from '../../GameHeader.vue';
    import GameInfo from '../GameInfo.vue';
    import Fireworks from '../Fireworks.vue';
    import ResultImage from '../ResultImage.vue';

    const DEFAULT_VOLUME = 0.6;
    const SECONDS_IN_MINUTE = 60;

    function DEFAULT_DATA() {
        return {
            assembleTheNumberTable: [],
            assembleTheNumberTableCells: [],
            amountOfNumbers: null,
            searchNumber: null,
            badAnswers: 0,
            goodAnswers: 0,
            amountOfExamples: 0,
            isNextExampleButtonShown: false,
            headerKey: false,
            infinityModeCompleted: false,
            termsOfSearchNumber: [0, 0],
            sumOfClickedCells: null,
        };
    }

    export default {
        mixins: [playBoxGameMixin, EndGameMixin],
        created() {
            window.addEventListener('beforeunload', this.beforeTabClosed);
        },
        beforeDestroy() {
            this.beforeTabClosed();
            window.removeEventListener('beforeunload', this.beforeTabClosed);
        },
        watch: {
            isTimeOver(val) {
                if (val) {
                    this.gameFinished = true;
                    this.stopTimer();
                    this.sendStat();
                }
            },
        },
        data() {
            return DEFAULT_DATA();
        },
        updated() {
            if (!this.assembleTheNumberTableCells?.length) {
                this.$nextTick(() => {
                    let collectionOfTableCells = document.querySelectorAll('.assemble-the-number-table__press-item');
                    this.assembleTheNumberTableCells = chunk([...collectionOfTableCells], this.parameters.difficult);
                });
            }
        },
        methods: {
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.assignDataFromParameters();
                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);
                        this.beforeStart();
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.gameStarted = true;
                !this.timer && this.setTimer();
            },
            assignDataFromParameters() {
                this.amountOfNumbers = this.parameters.difficult * this.parameters.difficult;

                this.searchNumber =
                    this.generateRandomNumberInAllowedRange(this.minSearchNumberValue, this.maxSearchNumberValue) + 1;

                this.termsOfSearchNumber[0] = this.generateRandomNumberInAllowedRange(1, this.searchNumber);
                this.termsOfSearchNumber[1] = this.searchNumber - this.termsOfSearchNumber[0];

                this.assembleTheNumberTable = Array().fill([]);
                this.assembleTheNumberTable = this.generateTable();

                if (!this.timer) {
                    this.setTimer();
                }
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
            },
            generateTable() {
                let randomIndexesForCorrectAnswer = [];
                let lengthOfSide = this.parameters.difficult;
                let arrayOfNums = new Array(this.amountOfNumbers);

                for (let index = 0; index < this.amountOfNumbers; index++) {
                    let number = this.generateRandomNumberInAllowedRange(1, this.searchNumber);
                    while (number === this.searchNumber) {
                        number = this.generateRandomNumberInAllowedRange(1, this.searchNumber);
                    }
                    arrayOfNums[index] = number;
                }

                while (randomIndexesForCorrectAnswer.length !== this.numberOfTerms) {
                    let index = Math.floor(Math.random() * this.amountOfNumbers - 1) + 1;
                    if (!randomIndexesForCorrectAnswer.includes(index)) {
                        randomIndexesForCorrectAnswer.push(index);
                    }
                }

                if (this.termsOfSearchNumber[0] !== this.termsOfSearchNumber[1]) {
                    for (let i = 0; i < this.numberOfTerms; i++) {
                        if (!arrayOfNums.includes(this.termsOfSearchNumber[i])) {
                            arrayOfNums[+randomIndexesForCorrectAnswer[i]] = this.termsOfSearchNumber[i];
                        }
                    }
                } else {
                    arrayOfNums[+randomIndexesForCorrectAnswer[0]] = this.termsOfSearchNumber[0];
                    arrayOfNums[+randomIndexesForCorrectAnswer[1]] = this.termsOfSearchNumber[1];
                }

                arrayOfNums = shuffle(arrayOfNums);
                return chunk(arrayOfNums, lengthOfSide);
            },
            generateRandomNumberInAllowedRange(minValue, maxValue) {
                return Math.floor(Math.random() * (maxValue - minValue) + minValue);
            },
            handleClick(cell, rowIndex, numIndex) {
                let numberOfClickedCells = document.querySelectorAll('.assemble-the-number-table__press-item_green')
                    .length;
                let clickedCell = this.assembleTheNumberTableCells[rowIndex][numIndex];

                if (clickedCell.classList.contains('assemble-the-number-table__press-item_green')) {
                    this.sumOfClickedCells -= cell;
                    clickedCell.classList.remove('assemble-the-number-table__press-item_green');
                    return;
                }

                if (numberOfClickedCells > 1) {
                    this.$createjs.Sound.play('clickIncorrect');
                    this.toggleClassForElement(clickedCell, ['shake']);
                    return;
                }

                clickedCell.classList.add('assemble-the-number-table__press-item_green');
                this.sumOfClickedCells += cell;

                let selectedCells = document.querySelectorAll('.assemble-the-number-table__press-item_green');

                if (this.sumOfClickedCells === this.searchNumber && selectedCells.length === this.numberOfTerms) {
                    let searchNumber = document.querySelector('.play-box__search-number');
                    this.toggleClassForElement(searchNumber, ['play-box__search-number_green']);
                    this.sumOfClickedCells = 0;
                    this.goodAnswers++;
                    this.$createjs.Sound.play('clickCorrect');
                    this.processReply();
                } else if (
                    this.sumOfClickedCells !== this.searchNumber &&
                    selectedCells.length === this.numberOfTerms
                ) {
                    this.sumOfClickedCells = 0;
                    this.badAnswers++;
                    this.$createjs.Sound.play('clickIncorrect');
                    let searchNumber = document.querySelector('.play-box__search-number');
                    this.toggleClassForElement(searchNumber, ['play-box__search-number_red']);
                    for (let i = 0; i < selectedCells.length; i++) {
                        selectedCells[i].classList.remove('assemble-the-number-table__press-item_green');
                        this.toggleClassForElement(selectedCells[i], [
                            'shake',
                            'assemble-the-number-table__press-item_red',
                        ]);
                    }
                }
            },
            toggleClassForElement(element, toggledClasses) {
                for (let i = 0; i < toggledClasses.length; i++) {
                    element.classList.add(toggledClasses[i]);
                    setTimeout(() => {
                        element.classList.remove(toggledClasses[i]);
                    }, 800);
                }
            },
            processReply() {
                this.amountOfExamples += 1;
                if (
                    this.parameters.numOfExamples <= this.amountOfExamples &&
                    !this.parameters.infinityAmountOfExamples
                ) {
                    this.gameFinished = true;
                    this.stopTimer();
                } else {
                    this.stopTimer();
                    this.isNextExampleButtonShown = true;
                }
                this.sendStat();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.soundInstance.volume = DEFAULT_VOLUME;
            },
            getExample() {
                if (this.isNextExampleButtonShown && !this.isTimeOver) {
                    this.gameFinished = false;
                    this.setTimer();
                    this.isNextExampleButtonShown = false;
                }
                this.assembleTheNumberTableCells = null;
                this.assignDataFromParameters();
                this.$nextTick(this.beforeStart);
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.goodAnswers + this.badAnswers;
                this.gameStat.answers = this.goodAnswers;
                this.gameStat.chainOfAttempts = [];
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time !== 0 ? this.time : this.timeBeforeDestroy;
                this.gameStat.isCorrect = this.gameFinished;
                this.gameStat.parameters = cloneDeep(this.parameters);
                if (this.parameters.infinityAmountOfExamples) {
                    this.gameStat.parameters.numOfExamples = this.amountOfExamples;
                }
                this.gameStat.infinityAmountOfExamples = this.parameters.infinityAmountOfExamples;
                this.gameStat.infinityModeCompleted = this.infinityModeCompleted;
            },
            calculatePercent() {
                let total = this.goodAnswers + this.badAnswers;
                if (total > 0) {
                    return parseInt((this.goodAnswers / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            beforeTabClosed() {
                if (this.gameStarted && this.user.type === 'student') {
                    this.gameFinished = true;
                    this.infinityModeCompleted = true;
                    this.sendStat();
                }
                return null;
            },
        },
        computed: {
            timeExecutionString() {
                return (
                    this.$i18n.t('progress.progress_table_executionTime') +
                    ': ' +
                    this.time +
                    ' ' +
                    this.$i18n.tc('olympiads.olympiads_timeDeclination_seconds', this.time)
                );
            },
            maxSearchNumberValue() {
                return Math.pow(10, +this.parameters.digitCapacity) - 1;
            },
            minSearchNumberValue() {
                return Math.pow(10, +this.parameters.digitCapacity - 1);
            },
            isTimeOver() {
                return (
                    this.parameters &&
                    this.parameters.timeLimit &&
                    this.time >= this.parameters.timeLimit * SECONDS_IN_MINUTE
                );
            },
            numberOfTerms() {
                return this.termsOfSearchNumber.length;
            },
        },
        components: {
            Fireworks,
            GameInfo,
            GameHeader,
            AssembleTheNumberTable,
            ResultImage,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        height: 100%;
    }

    .play-box--warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__game {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex: 1 1 auto;
    }

    .play-box__example {
        height: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        padding: 0 10px;
        margin: 0 auto;
        @include media($xlg) {
            flex-direction: row;
            justify-content: center;
        }
        width: 100%;
    }

    .play-box__example-info-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        margin-bottom: 30px;
        @include media($xlg) {
            flex-direction: column;
            margin-bottom: 0;
            margin-right: 60px;
            align-items: center;
        }
        @include media($xxl) {
            margin-right: 100px;
        }
    }

    .play-box__search-number-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin-right: 20px;
        @include media($xlg) {
            margin-right: 0;
            margin-bottom: 16px;
        }
    }

    .play-box__header {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        margin-bottom: 16px;
        text-align: center;
    }

    .play-box__search-number_red {
        color: $x-color-white !important;
        background-color: $hsl-color-orange-64 !important;
    }

    .play-box__search-number_green {
        color: $x-color-white !important;
        background-color: $hsl-color-green-58 !important;
    }

    .play-box__search-number_default {
        background-color: $hsl-color-gray-97;
        color: $hsl-color-green-58;
    }

    .play-box__search-number-wrapper {
        background-color: $x-color-white;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 86px;
        height: 52px;
        font-size: 22px;
        @include media($sm) {
            width: 126px;
            height: 73px;
        }
        @include media($md) {
            width: 142px;
            height: 85px;
        }
        @include media($lg) {
            width: 165px;
            height: 102px;
        }
        @include media($xlg) {
            width: 181px;
            height: 113px;
        }
    }

    .play-box__search-number {
        border-radius: $border-radius-10;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 76px;
        height: 42px;
        font-size: 22px;
        @include media($sm) {
            width: 106px;
            height: 53px;
            font-size: 28px;
        }
        @include media($md) {
            width: 122px;
            height: 65px;
            font-size: 38px;
        }
        @include media($lg) {
            width: 145px;
            height: 82px;
            font-size: 52px;
        }
        @include media($xlg) {
            width: 161px;
            height: 93px;
            font-size: 62px;
        }
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .result__buttons {
        align-items: center;
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__result {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        flex-grow: 5;
    }

    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-shrink: 1;
        flex-wrap: wrap;
        width: 100%;
        @include stack-space($space-base);
        flex-direction: row;
        @include media($md) {
            flex-wrap: nowrap;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        width: 50%;
        color: $hsl-color-green-58;
        @include stack-space($space-base);
        @include media($sm) {
            width: 50%;
            font-size: 28px;
            @include stack-space(0);
        }
        @include media($md) {
            width: unset;
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 20px;
        }
        @include media($lg) {
            font-size: 32px;
        }
        @include media($xlg) {
            font-size: 42px;
        }
        &:first-child {
            order: 1;
            @include media($md) {
                order: unset;
            }
        }
        &:last-child {
            order: 1;
            @include media($md) {
                order: unset;
            }
        }
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }

    .play-box__reaction-side--incorrect {
        color: $hsl-color-red-62;
    }

    .play-box__reaction-image {
        @include stack-space($space-base);
        order: 3;
        margin: 0 auto;
        @include media($md) {
            order: unset;
            margin: unset;
        }
    }
</style>
